import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Box, CircularProgress, Grid } from '@mui/material';
import { isEmpty, orderBy } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fileUploadToS3,
  getDefaultMailTemplatesByType,
  getPresignedURLForPut,
  getUserMailTemplatesByType,
  IBulkEmail,
  IBulkEmailAttachment,
  ICandidate,
  JobType,
  replacePlaceHolders,
  sendBulkEmailCandidateProfiles,
  getLatestCvOfCandidates,
  ICandidateRecentConversation,
  ITemplateDTO,
} from 'wisestep-api';
import {
  BulkEmailForm,
  CardVariant,
  IUserDTO,
  SmtpSetupConfigIcon,
  Spinner,
  TemplateListing,
  WsCard,
  WsColors,
  WsDrawer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { IApiResponseData, IEmailFormData } from '../../../../../components/BulkEmail/BulkEmail';
import {
  useFetchRecruiterSMTPAccounts,
  validateAllEmails,
} from '../../../../../components/BulkEmail/helper/apiCalls';
import DefaultSubmissionTemplateCustomisationTable from '../../../../../components/DefaultSubmissionTemplateCustomisationTable';
import { useSKUModalStore } from '../../../../../components/skuModal/SKUModalStore';
import { useUserContext, useWorkspaceContext } from '../../../../../context';
import { MULTIPLE_SUBMISSION_TEMPLATES } from '../../../../../features/templateSettings/helper/constants';
import { failed, success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import { getFavouredTemplatesFirst } from '../../../../templateSettings/helper/TemplateSettingsHelper';
import {
  getDefaultEmailBodyCustomized,
  getDefaultAttachments,
  defaultSubmissionTableData,
} from '../../helper/shareCandidateProfileEmailHelper';

interface IShareCandidateProfileEmail {
  selectedCandidateEntitys?: ICandidateRecentConversation[];
  candidatesList: ICandidate[];
  open: boolean;
  handleClose: () => void;
}

const ShareCandidateProfileEmail = ({
  open,
  handleClose,
  candidatesList,
  selectedCandidateEntitys = [],
}: IShareCandidateProfileEmail) => {
  const user = useUserContext().userDTO as IUserDTO;
  const workspaceContext = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [columnNamesForDefaultTable, setColumnNamesForDefaultTable] = useState<
    Record<string, unknown>[]
  >(defaultSubmissionTableData);
  const navigate = useNavigate();

  const [defaultAttachmentFiles, missingCandidates] = getDefaultAttachments(candidatesList);
  const [emailFormData, setEmailFormData] = useState<IEmailFormData>({
    attachments: defaultAttachmentFiles,
    jobCode: null,
    job: null,
    totalCount: 0,
    selectedTemplateBody: getDefaultEmailBodyCustomized(candidatesList, columnNamesForDefaultTable),
    selectedTemplateSubject: '',
    selectedTemplateName: '',
    ccList: [],
    bccList: [],
    toList: [],
  });
  const candidate: ICandidate = candidatesList[0];
  const [openTemplates, setOpenTemplates] = useState<boolean>(false);
  const [loadedTemplatesData, setLoadedTemplatesData] = useState<IApiResponseData>({
    jobs: [],
    templates: [],
  });
  const [fromEmailSignature, setFromEmailSignature] = useState<string>('');
  const accountData = useFetchRecruiterSMTPAccounts();
  const smtpAccounts = accountData.data;
  const [loading, setLoading] = useState<boolean>(true);
  const setModal = useSKUModalStore((state) => state.setModal);
  const orderedSmtpAccounts = !isEmpty(smtpAccounts)
    ? orderBy(smtpAccounts, ['isDefault'], [true]).reverse()
    : [];

  const [candidateTableSelectedFields, setCandidateTableSelectedFields] = useState(
    defaultSubmissionTableData
  );
  const [templateBody, setTemplateBody] = useState<string>('');
  useEffect(() => {
    if (smtpAccounts) setFromEmailSignature(smtpAccounts[0]?.signatureText ?? '');
  }, [smtpAccounts]);
  useEffect(() => {
    if (accountData.isLoaded) {
      setLoading(false);
    }
  }, [accountData.isLoaded]);

  const handleOpenTemplates = () => setOpenTemplates(true);

  const handleCloseTemplates = () => setOpenTemplates(false);

  const manageSmtpAccounts = () =>
    openUrlInNewTab(`https://${workspaceContext.link}.wisestep.com/email-settings/`);

  const [refresh, setRefresh] = useState<boolean>(true);

  const getAllTemplates = async () => {
    let allTemplates: ITemplateDTO[] = [];
    if (candidatesList.length === 1) {
      const getTemplateData = await getUserMailTemplatesByType('submission');
      const getTemplateData2 = await getUserMailTemplatesByType(MULTIPLE_SUBMISSION_TEMPLATES);
      const defaultTemplateData = await getDefaultMailTemplatesByType('submission');

      allTemplates = getFavouredTemplatesFirst(
        [...getTemplateData.data, ...getTemplateData2.data, ...defaultTemplateData.data],
        user
      );
    } else if (candidatesList.length > 1) {
      const getTemplateData2 = await getUserMailTemplatesByType(MULTIPLE_SUBMISSION_TEMPLATES);

      allTemplates = getTemplateData2.data;
    }

    setLoadedTemplatesData((prev) => ({
      ...prev,
      templates: allTemplates,
    }));
  };

  const providerByHost = {
    'smtp.gmail.com': 'gsuite',
    'smtp.outlook.com': 'office',
    'smtp.mail.yahoo.com': 'yahoo',
    'smtpout.secureserver.net': 'godaddyPremium',
    default: 'Custom',
  };

  const getProviderByHost = (host) => providerByHost[host] || providerByHost.default;

  const send = async (data) => {
    if (data.to.length > 50) {
      setNotification(failed('Please select no more than 50 emails to send email!'));
      return;
    }
    const missedResumes: IBulkEmailAttachment[] = [];

    if (missingCandidates.length > 0) {
      const missingCandidateIds = selectedCandidateEntitys
        .filter((candidate) => missingCandidates.includes(candidate.candidateEntity?.email))
        .map((candidate) => candidate.candidateEntity?.candidateId);
      try {
        const missedAttachmentResumeData = await getLatestCvOfCandidates(missingCandidateIds);
        missedAttachmentResumeData.data.forEach((candidateResume) => {
          if (candidateResume)
            missedResumes.push({
              attachementURL: candidateResume.resumeLocation
                ? candidateResume.resumeLocation.split('?')[0]
                : '',
              attachementName: candidateResume.resumeFileOriginalName,
            });
        });
      } catch (err: any) {
        console.error('Error fetching attachments:', err);
      }
    }
    const bulkEmailData: IBulkEmail = {
      ...data,
      interactions: [],
      interactionIds: [],
      jobId: emailFormData.job?.id || '',
      jobTitle: emailFormData.job?.title || '',
      job: emailFormData.job ?? {},
      jobCode: emailFormData.job?.jobCode || '',
      attachments: [...emailFormData.attachments, ...missedResumes].filter(
        (resume) => resume.attachementURL != ''
      ),
    };
    bulkEmailData.emailProvider = getProviderByHost(
      smtpAccounts
        .filter((account) => account.fromEmail == bulkEmailData.from)
        .map((data) => data.host)[0]
    );
    bulkEmailData.mailToType = 'CandidateProfilesToAny';
    bulkEmailData.canSendIndividualEmail = false;

    try {
      await sendBulkEmailCandidateProfiles(bulkEmailData);
      setEmailFormData({
        attachments: [],
        jobCode: null,
        job: {},
        totalCount: 0,
        selectedTemplateBody: '',
        selectedTemplateSubject: '',
        selectedTemplateName: '',
        bccList: [],
        ccList: [],
      });
      handleClose();
      setNotification(success('Email Sent Successfully'));
    } catch (err: any) {
      if (err.response.status == 402) {
        setModal({ object: err.response.data, type: 'count' });
      }
      setNotification(failed(err.message));
    }
  };

  const getTemplateId = async (selectedTemplateId: string) => {
    setRefresh(false);
    const selectedTemplate = loadedTemplatesData.templates.find(
      (template) => template.id === selectedTemplateId
    );
    if (!selectedTemplate) {
      return;
    }

    const bulkEmailData: IBulkEmail = {
      interactions: candidate ? [candidate] : [],
      jobId: candidate?.jobId ?? '',
      job: {
        id: candidate?.jobId ?? '',
        title: candidate?.mappedJobTitle ?? '',
        jobCode: candidate?.jobCode ?? '',
        locations: candidate?.jobLocations ?? [],
        accountManagerName: candidate?.jobAccountManagerName ?? '',
        clientRecruiterName: candidate?.jobClientRecruiterName ?? '',
        jobType: JobType[candidate?.jobType ?? 'None'],
      },
      attachments: emailFormData.attachments,
      subject: selectedTemplate.subject ?? '',
      body: selectedTemplate.body ?? '',
      cc: selectedTemplate.cc ?? [],
      from: '',
      to: [],
      title: '',
    };
    if (selectedTemplate.templateType == MULTIPLE_SUBMISSION_TEMPLATES) {
      bulkEmailData.body =
        bulkEmailData.body?.slice(0, bulkEmailData.body?.indexOf('{{')) +
        getDefaultEmailBodyCustomized(
          candidatesList,
          JSON.parse(selectedTemplate.submissionFields ?? '')
        ) +
        bulkEmailData.body?.slice(bulkEmailData.body?.indexOf('</table>'));
    }

    const response = await replacePlaceHolders(bulkEmailData);
    const jobUrl = '';
    setEmailFormData((prev) => ({
      ...prev,
      selectedTemplateName: selectedTemplate.name ?? '',
      selectedTemplateBody: response.data.body.replace('{{job.jobUrl}}', jobUrl) ?? '',
      selectedTemplateSubject:
        response.data.subject?.replace(/<[^>]+>/g, '')?.replace('{{job.jobUrl}}', jobUrl) ?? '',
      ccList: selectedTemplate.cc ?? [],
      bccList: selectedTemplate.bcc ?? [],
    }));
    setTemplateBody(response.data.body.replace('{{job.jobUrl}}', jobUrl) ?? '');
    setCandidateTableSelectedFields(JSON.parse(selectedTemplate.submissionFields ?? ''));
    setRefresh(true);
  };

  const openTemplateList = () => {
    getAllTemplates();
    handleOpenTemplates();
  };

  const fromEmailSignatureUpdater = (fEmail: string) => {
    const signatureText =
      smtpAccounts
        .filter((account) => account.fromEmail == fEmail)
        .map((data) => data.signatureText)[0] ?? '';
    setFromEmailSignature(signatureText);
  };

  const removeSelectedTemplate = () => {
    setEmailFormData((prev) => ({
      ...prev,
      selectedTemplateName: '',
      selectedTemplateBody: '',
      selectedTemplateSubject: '',
    }));
  };

  const uploadAttachment = async (file: File) => {
    if (emailFormData.attachments) {
      try {
        const response = await getPresignedURLForPut({ fileName: file.name, fileType: file.type });
        const tempData = emailFormData;
        tempData.attachments = [
          ...emailFormData.attachments,
          ...[
            {
              attachementName: file.name,
              attachementURL: response.data.preSignedUrl.split('?')[0],
            },
          ],
        ];
        setEmailFormData(tempData);
        await fileUploadToS3(file, response.data.preSignedUrl);
        setNotification(success('Uploaded attachment(s) successfully!'));
      } catch (err: any) {
        console.log(err.message);
        setNotification(failed(err.message));
      }
    }
  };

  const onApply = (selectedValues: Record<string, unknown>[]) => {
    const updatedEmailFormData = { ...emailFormData };
    const templateBodyHeader = templateBody?.slice(0, templateBody?.indexOf('<table'));
    const templateBodyFooter = templateBody?.slice(templateBody?.indexOf('</table>'));
    updatedEmailFormData.selectedTemplateBody =
      templateBodyHeader +
      getDefaultEmailBodyCustomized(candidatesList, selectedValues) +
      templateBodyFooter;
    setEmailFormData(updatedEmailFormData);
    setColumnNamesForDefaultTable(selectedValues);
  };

  return (
    <WsDrawer
      style={{ zIndex: '1300' }}
      iconType="cross"
      size="full"
      title="Send Email"
      open={open}
      onClose={handleClose}
      rightChildren={
        candidate?.jobCode != null && candidatesList.length < 2 ? (
          <Grid container direction="row" spacing={2} pb={1}>
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
              color={WsTypoColors.PRIMARY}
            >
              {candidate?.jobCode}
            </WsTypography>
            &nbsp;
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              bold={true}
              color={WsTypoColors.PRIMARY}
            >
              {candidate?.mappedJobTitle}
            </WsTypography>
          </Grid>
        ) : (
          ''
        )
      }
    >
      <Grid container>
        {orderedSmtpAccounts.length > 0 && (
          <Grid item xs={3}>
            {refresh ? (
              <DefaultSubmissionTemplateCustomisationTable
                onSelectValues={onApply}
                defaultSelectedValues={candidateTableSelectedFields}
              />
            ) : (
              <Spinner />
            )}
          </Grid>
        )}

        <Grid item xs={orderedSmtpAccounts.length > 0 ? 9 : 12}>
          {!loading ? (
            <>
              {orderedSmtpAccounts.length > 0 ? (
                <BulkEmailForm
                  jobTitle={null}
                  jobCode={null}
                  template={emailFormData.selectedTemplateBody}
                  subjectTemplate={emailFormData.selectedTemplateSubject}
                  templateName={emailFormData.selectedTemplateName}
                  ccList={emailFormData.ccList}
                  bccList={emailFormData.bccList}
                  toEmailList={emailFormData.toList}
                  fromEmailList={orderedSmtpAccounts.map((account) => account.fromEmail)}
                  manageSenderAccounts={manageSmtpAccounts}
                  changeJob={() => {}}
                  openTemplatesList={openTemplateList}
                  showImportTemplates={true}
                  removeSelectedTemplate={removeSelectedTemplate}
                  uploadAttachment={uploadAttachment}
                  handleAttachmentError={() =>
                    setNotification(failed('The file size must be less than 25MB!'))
                  }
                  fromEmailSignature={fromEmailSignature}
                  updateFromEmailSignature={(fEmail) => fromEmailSignatureUpdater(fEmail)}
                  submitAction={send}
                  handleClose={handleClose}
                  defaultAttachmentFileNames={defaultAttachmentFiles.map(
                    (attachment) => attachment.attachementName
                  )}
                  footerSize="large"
                  deleteAttachmentHandler={(deletedFileName: string) => {
                    const tempData = emailFormData;
                    tempData.attachments = emailFormData.attachments.filter(
                      (singleAttachment) => singleAttachment.attachementName !== deletedFileName
                    );
                    setEmailFormData(tempData);
                  }}
                  deleteAllAttachments={() => {
                    const tempData = { ...emailFormData };
                    tempData.attachments = [];
                    setEmailFormData(tempData);
                  }}
                  setBody={(body, to, cc, bcc) => {
                    setTemplateBody(body);
                    setEmailFormData((prevData) => ({
                      ...prevData,
                      toList: to,
                      ccList: cc,
                      bccList: bcc,
                    }));
                  }}
                  validateEmails={validateAllEmails}
                />
              ) : (
                <Grid pt={20}>
                  <WsCard
                    cardType={CardVariant.WITH_OUT_BG}
                    title="No Email Account"
                    description="Connect your email account and send emails to single or multiple candidates"
                    middleComponent={<SmtpSetupConfigIcon />}
                    isButton={true}
                    buttonText="Setup email account"
                    btnStartIcon={<AddOutlinedIcon />}
                    onClick={() => {
                      handleClose();
                      navigate('/email-settings/');
                    }}
                  />
                </Grid>
              )}
            </>
          ) : (
            <Box textAlign="center" pt={25}>
              <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
            </Box>
          )}
        </Grid>
      </Grid>

      <WsDrawer
        size="large"
        title={'Select a Template'}
        iconType="cross"
        open={openTemplates}
        onClose={handleCloseTemplates}
        style={{ zIndex: '1302' }}
      >
        <TemplateListing
          templates={loadedTemplatesData.templates}
          handleClose={handleCloseTemplates}
          getTemplateId={getTemplateId}
        />
      </WsDrawer>
    </WsDrawer>
  );
};

export default ShareCandidateProfileEmail;
